<template>
  <b-container>
    <b-row>
      <b-col lg="6" xs="12" id="configuration-column">
        <DemoGuide :handle-mouse-leave="setActiveWidget">
          <DemoGuideStep :handle-mouse-over="setActiveWidget" :index="0">
            <font-awesome-icon icon="brush" slot="icon" />
            <template slot="content">
              <h5>{{ $t("Add your brand") }}</h5>
              <div class="demo-guide-step__description">
                {{
                  $t(
                    "Upload your logo and pick a color to make your tracking page stand out."
                  )
                }}
              </div>

              <!-- <b-button size="xs" class="demo-guide-step__button" variant="outline-primary"><font-awesome-icon icon="eye-dropper"/> Pick color</b-button> -->
              <div class="demo-guide-step__input-wrap">
                <label for="logo" class="demo-guide-step__file-button">
                  <input
                    name="logo"
                    id="logo"
                    type="file"
                    accept="image/*"
                    placeholder="Pick logo"
                    @change="setLogo"
                  />
                  <span class="demo-guide-step__file-button-inner"
                    ><font-awesome-icon icon="cloud-upload-alt" />{{
                      $t("Pick logo")
                    }}</span
                  >
                </label>
                <div
                  class="demo-guide__color-swatch"
                  v-for="(color, index) in brandColorSuggestions"
                  @click="setBrandColor(color)"
                  :key="`${color}${index}`"
                  :style="{ backgroundColor: color }"
                ></div>
                <b-form-checkbox
                  v-model="showIllustration"
                  name="check-button"
                  switch
                >
                  <small
                    >{{ $t("Illustration") }}
                    <strong>{{
                      showIllustration ? $t("On") : $t("Off")
                    }}</strong></small
                  >
                </b-form-checkbox>
              </div>
            </template>
          </DemoGuideStep>

          <DemoGuideStep :handle-mouse-over="setActiveWidget" :index="1">
            <font-awesome-icon :icon="['fab', 'instagram']" slot="icon" />
            <template slot="content">
              <h5>{{ $t("Add your Instagram") }}</h5>
              <div class="demo-guide-step__description">
                {{
                  $t(
                    "Make people engage with your brand by adding an Instagram post."
                  )
                }}
              </div>
              <label for="instagram-handle" class="demo-guide-step__label">{{
                $t("Instagram handle")
              }}</label>
              <b-form-input
                id="instagram-handle"
                v-model="instagramPost.handle"
                ref="instagram-url"
                class="demo-guide-step__input"
                type="text"
                :placeholder="$t('e.g. @sendcloud_nl')"
              ></b-form-input>

              <label
                for="instagram-caption"
                class="demo-guide-step__label demo-guide-step__input-wrap--instagram-label"
                >{{ $t("Instagram image and caption") }}</label
              >
              <div class="demo-guide-step__input-wrap">
                <b-form-input
                  id="instagram-caption"
                  class="demo-guide-step__input"
                  type="text"
                  ref="instagram-caption"
                  v-model="instagramPost.caption"
                  :placeholder="$t(`e.g. Try Europe's best shipping tool`)"
                ></b-form-input>
                <label
                  for="instagram-image"
                  class="demo-guide-step__file-button"
                >
                  <input
                    name="instagram-image"
                    id="instagram-image"
                    type="file"
                    accept="image/png, image/jpeg, image/svg"
                    :placeholder="$t('Pick image')"
                    @change="setInstagramImage"
                  />
                  <span class="demo-guide-step__file-button-inner"
                    ><font-awesome-icon icon="cloud-upload-alt" />
                    {{ $t("Pick image") }}</span
                  >
                </label>
              </div>
            </template>
          </DemoGuideStep>

          <DemoGuideStep :handle-mouse-over="setActiveWidget" :index="2">
            <font-awesome-icon icon="gift" slot="icon" />
            <template slot="content">
              <h5>{{ $t("Add a Banner Ad") }}</h5>
              <div class="demo-guide-step__description">
                {{
                  $t(
                    "Promote your products or services by adding a nice promotion to your tracking page."
                  )
                }}
              </div>
              <label for="promotion-text" class="demo-guide-step__label">{{
                $t("Your promotion text")
              }}</label>
              <div class="demo-guide-step__input-wrap">
                <b-form-input
                  id="promotion-text"
                  class="demo-guide-step__input"
                  type="text"
                  ref="promotion-text"
                  @focus="focusAndSelect('promotion-text')"
                  v-model="bannerAdSettings.text"
                  :placeholder="$t(`e.g. Try Europe's best shipping tool`)"
                ></b-form-input>
                <label for="banner-ad" class="demo-guide-step__file-button">
                  <input
                    name="banner-ad"
                    id="banner-ad"
                    type="file"
                    accept="image/png, image/jpeg, image/svg"
                    :placeholder="$t('Pick image')"
                    @change="setBannerAdImage"
                  />
                  <span class="demo-guide-step__file-button-inner"
                    ><font-awesome-icon icon="cloud-upload-alt" />
                    {{ $t("Pick image") }}</span
                  >
                </label>
              </div>
              <small class="text-muted demo-guide-step__help">{{
                $t("Upload an image of about 400 x 800.")
              }}</small>
            </template>
          </DemoGuideStep>
        </DemoGuide>
      </b-col>
      <b-col lg="6" xs="12" id="demo-column">
        <DemoStand :flipToLeft="true" :scale="0.5">
          <div slot="demo-content" class="tracking-page">
            <img
              :src="logoUrl"
              ref="logo"
              class="tracking-page__widget tracking-page__logo"
              height="90"
              :class="{ 'is-editing': activeWidget === 0 }"
            />
            <UiStatusHistory
              class="tracking-page__widget tracking-page__ui-status-history"
              :class="{ 'is-editing': activeWidget === 0 }"
              locale="nl"
              :themeColor="brandColor"
              :parcel="parcel"
              :showIllustration="showIllustration"
            ></UiStatusHistory>
            <b-row align-v="stretch" class="tracking-page__second-row">
              <b-col lg="6" xs="12" v-show="showInstagram">
                <div
                  class="ig-widget"
                  :class="{ 'is-editing': activeWidget === 1 }"
                >
                  <div class="ig-widget-header">
                    <div class="ig-widget-user">
                      <div class="ig-widget-avatar"></div>
                      <h4 class="ig-widget-heading">
                        {{ instagramPost.handle }}
                      </h4>
                    </div>
                    <button class="ig-widget-post-button">Visit profile</button>
                  </div>
                  <div
                    class="widget-image"
                    :style="{ backgroundImage: `url(${instagramPost.image})` }"
                  ></div>
                  <div class="ig-caption">
                    <p class="widget-paragraph">
                      {{ instagramPost.caption }}
                    </p>
                  </div>
                </div>
              </b-col>
              <b-col :lg="showInstagram ? 6 : 12" xs="12">
                <UiBannerAd
                  class="tracking-page__widget"
                  :bannerAdSettings="bannerAdSettings"
                  :class="{ 'is-editing': activeWidget === 2 }"
                />
              </b-col>
            </b-row>
            <transition name="fade">
              <div class="demo-content__overlay" v-if="isEditingWidget"></div>
            </transition>
          </div>
        </DemoStand>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import DemoGuide from "@/components/DemoGuide.vue";
import DemoGuideStep from "@/components/DemoGuideStep.vue";
import DemoStand from "@/components/DemoStand.vue";
import parcel from "@/data/parcel.js";
import { UiBannerAd, UiStatusHistory } from "@sendcloud/ui-library";
import {
BCol,
BContainer,
BFormCheckbox,
BFormInput,
BRow,
} from "bootstrap-vue";

import * as Vibrant from "node-vibrant";

export default {
  name: "TrackingPage",
  components: {
    BContainer,
    BRow,
    BCol,
    BFormInput,
    BFormCheckbox,
    UiStatusHistory,
    UiBannerAd,
    DemoStand,
    DemoGuide,
    DemoGuideStep,
  },
  data() {
    return {
      parcel,
      showIllustration: true,
      showInstagram: true,
      activeWidget: null,
      instagramUrl: "http://instagram.com/p/BxWuEYajHtr",
      promotionText: null,
      logoUrl:
        "https://media.sendcloud.sc/brands/27334/9b25d9a2d551c20c5e9968331d929d46_thumbnail.png",
      brandColor: "#41beab",
      brandColorSuggestions: [],
      instagramPost: {
        handle: "@jimmyjoyfood",
        caption: `🤔🍨 "Can I make ice cream out of Plenny Shake Pistachio?" YES, you can!

Especially for you, we've prepared an easy, delicious Vegan Pistachio gelato recipe 💚`,
        image: require("@/assets/jimmyjoy_placeholder.jpeg"),
      },
      bannerAdSettings: {
        url: null,
        bg_image:
          "https://jimmyjoy-test.shipping-portal.com/tracking/Artboard_QiKkFyE.png",
        text: "100% Complete nutrition",
        text_2: "Saves you time, money and effort",
        text_btn: "Shop now",
        bg_image_active: true,
        bg_color: "#ebfff8",
        text_position_x: "center",
        text_position_y: "top",
        text_color: "black",
        text_2_color: "black",
        text_btn_color: "white",
        text_btn_bg_color: "black",
      },
    };
  },
  computed: {
    isEditingWidget() {
      return !!this.activeWidget || this.activeWidget === 0;
    },
  },
  methods: {
    setActiveWidget(index) {
      this.activeWidget = index;
    },
    focusAndSelect(ref) {
      if (ref === "promotion-text")
        this.resetBannerAd(this.brandColorSuggestions[1], false);
      this.$refs[ref].select();
    },
    async setInstagramUrl(e) {
      this.instagramUrl = e.currentTarget.value;
    },
    setLogo(e) {
      let file = e.target.files;
      if (!file || file.length < 1) return;
      file = file[0];

      let reader = new FileReader();
      reader.onload = (event) => {
        this.logoUrl = event.target.result;
        this.extractAndSetBrandColors(event.target.result);
      };
      reader.readAsDataURL(file);
    },
    setBannerAdImage(e) {
      let file = e.target.files;
      if (!file || file.length < 1) return;
      file = file[0];
      let reader = new FileReader();
      reader.onload = (event) => {
        this.bannerAdSettings.bg_image = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    setInstagramImage(e) {
      let file = e.target.files;
      if (!file || file.length < 1) return;
      file = file[0];
      let reader = new FileReader();
      reader.onload = (event) => {
        this.instagramPost.image = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    async extractAndSetBrandColors(imageSrc) {
      this.brandColorSuggestions = [];
      let img = new Image();
      img.src = imageSrc;
      await Vibrant.from(img)
        .getPalette()
        .then((palette) => {
          if (!palette) return;
          Object.keys(palette).forEach((swatch, index) => {
            if (index > 2) return;
            let color = palette[swatch].getHex();
            this.brandColorSuggestions.push(color);
          });
        });
      this.setBrandColor(this.brandColorSuggestions[0]);
      this.resetBannerAd(this.brandColorSuggestions[1], true);
    },
    setBrandColor(color) {
      this.brandColor = color;
    },
    resetBannerAd(color, resetBgImage) {
      this.bannerAdSettings.bg_color = color;
      this.bannerAdSettings.text = this.$t("Your promotion here");
      this.bannerAdSettings.text_2 = null;
      if (resetBgImage) {
        this.bannerAdSettings.bg_image = null;
      }
    },
  },
};
</script>

<style scoped>
.tracking-page {
  font-family: "Source Sans Pro";
  width: 100%;
  display: flex;
  padding: 50px;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
}

.tracking-page__logo {
  background: #f3f6f8;
  padding: 10px;
  margin-bottom: 30px;
  border-radius: 7px;
}

.tracking-page__second-row {
  margin-top: 30px;
}

@media screen and (max-width: 567px) {
  .tracking-page {
    padding: 15px;
  }
  .tracking-page__logo {
    margin-bottom: 10px;
    max-height: 60px;
  }
  .tracking-page__second-row {
    margin-top: 20px;
  }
}

.demo-guide__color-swatch {
  width: 28px;
  height: 28px;
  border-radius: 30px;
  border: none;
  display: inline-block;
  margin-right: 4px;
}

.demo-guide__color-swatch:focus {
  border: none;
}

.demo-guide__color-swatch-help {
  margin-right: 10px;
}

.tracking-page__widget {
  z-index: 0;
  transition: transform 0.5s;
  will-change: transform;
}

.is-editing {
  z-index: 10;
  position: relative;
  transform: scale(1.1);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Demo guide */

.demo-content__overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1;
  top: 0;
}

.demo-guide-step__description {
  margin-top: 10px;
  margin-bottom: 20px;
}

.demo-guide-step__file-button {
  position: relative;
  flex-shrink: 0;
  padding: 5px 0;
  margin: 0;
  display: inline-block;
  width: auto;
  transition: background 0.2s;
  cursor: pointer;
}

.demo-guide-step__file-button input {
  display: none;
}
.demo-guide-step__file-button:hover {
  background: rgba(0, 0, 0, 0.08);
}

.demo-guide-step__file-button-inner {
  font-size: 0.9em;
  padding: 6px 12px;
  border: 1px solid #ced4da;
  border-radius: 20px;
  margin-right: 10px;
}
.demo-guide-step__file-button-inner svg {
  margin-right: 5px;
}

.demo-guide-step__button {
  border-radius: 20px;
  margin-right: 10px;
  background: transparent;
  padding: 6px 12px;
  color: var(--full-blue);
  border: 1px solid #ced4da;
  flex-shrink: 0;
}

.demo-guide-step__button svg {
  margin-right: 5px;
}

.demo-guide-step__input-wrap {
  display: flex;
  align-items: center;
}

.demo-guide-step__input-wrap .demo-guide-step__button {
  margin-left: 10px;
}

.demo-guide-step__input-wrap--instagram-label {
  margin-top: 10px;
}

.demo-guide-step__label {
  font-family: "BeatriceMedium";
  font-size: 0.85em;
  padding-left: 10px;
}

.demo-guide-step__help {
  padding-left: 10px;
  display: inline-block;
  margin-top: 5px;
}

.demo-guide-step__input {
  border-radius: 25px;
  font-size: 0.9em;
}
.demo-guide-step__input + .demo-guide-step__file-button {
  margin-left: 10px;
}

.tracking-page__ui-status-history {
  width: 100%;
  flex-shrink: 0;
}

.demo-guide__instagram-toggle {
  margin-top: 10px;
  margin-left: 10px;
}

/* Instagram widget */
.ig-widget {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0px;
  background: rgb(255, 255, 255);
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.07);
  transition: transform 0.5s;
  will-change: transform;
}

@media screen and (max-width: 992px) {
  .ig-widget {
    margin-bottom: 20px;
  }
}

.ig-widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px;
}
.widget-image {
  display: flex;
  height: 328px;
  padding: 16px;
  background: rgb(240, 240, 240);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}
.ig-caption {
  display: flex;
  padding: 16px;
}
.ig-widget-user {
  display: flex;
  align-items: center;
}
.ig-widget-post-button {
  padding: 4px 8px;
  border: 1px solid #d9dee2;
  background: #f3f6f8;
  border-radius: 5px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.07);
  transition: background 0.25s ease-in-out 0s;
  font-weight: 500;
  font-size: 0.9rem;
}

.widget-paragraph {
  display: inline;
  font-size: 0.8rem;
}
.ig-widget-avatar {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 32px;
  height: 32px;
  background: url("~@/assets/instagram-logo.svg") 50% 50% / contain no-repeat;
  border: 2px solid #ffffff;
  border-radius: 100%;
}
.ig-widget-heading {
  font-size: 1rem;
  margin-left: 8px;
  margin-bottom: 0;
}
</style>
